import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { EnumStatusCode } from 'src/app/enum/enumStatusCode';
import { UserService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-e-bill-apply',
  templateUrl: './e-bill-apply.component.html',
  styleUrls: ['./e-bill-apply.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EBillApplyComponent {
  isBrowser: boolean;

  /** 目前步驟 (1–4) */
  currentStep = 1;

  // ── Step 1：填寫基本資料 ──────────────────────────────────────────────────
  s1IdNumber = '';
  s1Phone = '';
  s1CaptchaUrl = '';
  s1CaptchaToken = '';
  s1CaptchaCode = '';
  s1VerifCode = '';
  s1AgreeChecked = false;
  s1SmsSent = false;
  s1ErrorMsg = '';
  stepToken = '';

  // ── Step 2：身份驗證 ─────────────────────────────────────────────────────
  s2IssueDate = '';
  s2IssueLocation: string | null = null;
  s2IssueCategory: string | null = null;
  s2ErrorMsg = '';
  s2Validating = false;   // 驗證中旗標（禁用按鈕、顯示 loading）

  issueCategoryList: Array<{ label: string; value: string }> = [];
  issueSiteList: Array<{ label: string; value: string }> = [];

  // ── Step 3：申請電子帳單 ─────────────────────────────────────────────────
  s3Email = '';
  s3CaptchaUrl = '';
  s3CaptchaToken = '';
  s3CaptchaCode = '';
  s3VerifCode = '';
  s3EmailSent = false;

  s3ShowConfirm = false;                  // 確認送出對話框
  s3Submitting = false;
  s3ErrorMsg = '';

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private sanitizer: DomSanitizer,
    private userService: UserService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.loadStep1Captcha();
  }

  // ── 圖形驗證碼 ─────────────────────────────────────────────────────────────

  loadStep1Captcha() {
    this.userService.GetCaptchaImg().subscribe(res => {
      if (res.StatusCode === EnumStatusCode.Success) {
        this.s1CaptchaUrl = res.Entries.Base64;
        this.s1CaptchaToken = res.Entries.Token;
        this.s1CaptchaCode = '';
      }
    });
  }

  loadStep3Captcha() {
    this.userService.GetCaptchaImg().subscribe(res => {
      if (res.StatusCode === EnumStatusCode.Success) {
        this.s3CaptchaUrl = res.Entries.Base64;
        this.s3CaptchaToken = res.Entries.Token;
        this.s3CaptchaCode = '';
      }
    });
  }

  loadStep2IssueSiteList() {
    this.userService.GetIssueSiteList().subscribe({
      next: (res: any) => {
        this.issueSiteList = res?.StatusCode === EnumStatusCode.Success
          ? this.normalizeStep2CodeOptions(res.Entries)
          : [];
      },
      error: () => { this.issueSiteList = []; }
    });
  }

  loadStep2ApplyCodeList() {
    this.userService.GetApplyCodeList().subscribe({
      next: (res: any) => {
        this.issueCategoryList = res?.StatusCode === EnumStatusCode.Success
          ? this.normalizeStep2CodeOptions(res.Entries)
          : [];
      },
      error: () => { this.issueCategoryList = []; }
    });
  }

  loadStep2DropdownOptions() {
    this.loadStep2IssueSiteList();
    this.loadStep2ApplyCodeList();
  }

  private resolveApiErrorMessage(error: any, fallback: string): string {
    return error?.error?.Entries?.Message
      || error?.error?.Message
      || error?.message
      || fallback;
  }

  private normalizeCodeOptions(entries: any): Array<{ label: string; value: string }> {
    const source = Array.isArray(entries) ? entries : [];

    return source
      .map((x: any) => ({
        label: (x?.label ?? x?.Label ?? '').toString().trim(),
        value: (x?.value ?? x?.Value ?? '').toString().trim()
      }))
      .filter((x: { label: string; value: string }) => !!x.value);
  }

  private normalizeStep2CodeOptions(entries: any): Array<{ label: string; value: string }> {
    return this.normalizeCodeOptions(entries).map((x: { label: string; value: string }) => ({
      value: x.value,
      label: this.extractNameLabel(x.label, x.value)
    }));
  }

  private extractNameLabel(label: string, value: string): string {
    const raw = (label || '').trim();
    if (!raw) {
      return raw;
    }

    if (value && raw.startsWith(value)) {
      const rest = raw.slice(value.length).replace(/^[\s:：、._-]+/, '').trim();
      if (rest) {
        return rest;
      }
    }

    const byLeadingCode = raw.replace(/^\d{1,5}[\s:：、._-]+/, '').trim();
    return byLeadingCode || raw;
  }

  getSanitizedUrl(url: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // ── Step 1：取得 SMS 驗證碼 ─────────────────────────────────────────────────

  getStep1VerifCode() {
    this.s1ErrorMsg = '';
    if (!this.s1Phone) {
      this.s1ErrorMsg = '請輸入行動電話。';
      return;
    }

    // 呼叫後端發送簡訊
    this.userService.SendSMS({
      Mobile: this.s1Phone,
      CIdNumber: this.s1IdNumber
    }).subscribe(
      (res: any) => {
        const entries = res?.Entries;
        const returnCode = entries?.ResponseHeader?.ReturnCode;
        const returnMsg = entries?.ResponseHeader?.ReturnMsg || entries?.ResponseBody?.Data?.Message || res?.Message;
        const isSuccess = res?.StatusCode === 0 && (entries?.ResponseBody?.Data?.IsSuccess !== false);

        if (isSuccess) {
          this.s1SmsSent = true;
          const data = entries || res;
          // 開發模式：直接顯示驗證碼
          if (data.IsDevMode) {
            this.s1ErrorMsg = `✅ ${data.VerifyCode}`;
            alert(`[開發模式] 驗證碼：${data.VerifyCode}`);
          } else {
            this.s1ErrorMsg = '✅ 驗證碼已發送至您的手機，請於 30 分鐘內輸入。';
          }
        } else {
          const codeText = returnCode ? `（代碼: ${returnCode}）` : '';
          this.s1ErrorMsg = `❌ 簡訊發送失敗${codeText} ${returnMsg || '請稍後重試。'}`;
          this.s1SmsSent = false;
        }
      },
      error => {
        const err = error?.error;
        const errCode = err?.Entries?.ResponseHeader?.ReturnCode;
        const errMsg = err?.Entries?.ResponseHeader?.ReturnMsg || err?.Message || '伺服器連線失敗，請稍後重試。';
        const codeText = errCode ? `（代碼: ${errCode}）` : '';
        this.s1ErrorMsg = `❌ 簡訊發送失敗${codeText} ${errMsg}`;
        this.s1SmsSent = false;
      }
    );
  }

  // ── Step 1 → Step 2 ────────────────────────────────────────────────────────

  step1Submit() {
    this.s1ErrorMsg = '';
    if (!this.s1IdNumber.trim()) {
      this.s1ErrorMsg = '請輸入身分證字號。';
      return;
    }
    if (!/^[A-Z][12]\d{8}$/.test(this.s1IdNumber)) {
      this.s1ErrorMsg = '身分證字號格式錯誤。';
      return;
    }
    if (!this.s1Phone.trim()) {
      this.s1ErrorMsg = '請輸入行動電話。';
      return;
    }
    if (!/^09\d{8}$/.test(this.s1Phone)) {
      this.s1ErrorMsg = '行動電話格式錯誤（例：0912345678）。';
      return;
    }
    if (!this.s1VerifCode.trim()) {
      this.s1ErrorMsg = '請輸入簡訊驗證碼。';
      return;
    }
    if (!this.s1CaptchaCode.trim()) {
      this.s1ErrorMsg = '請輸入圖形驗證碼。';
      return;
    }
    if (!this.s1CaptchaToken) {
      this.s1ErrorMsg = '圖形驗證碼已失效，請重新整理後再試。';
      this.loadStep1Captcha();
      return;
    }
    if (!this.s1AgreeChecked) {
      this.s1ErrorMsg = '請勾選同意個人資料蒐集告知事項。';
      return;
    }

    // 呼叫後端驗證 Step1
    this.userService.VerifyStep1({
      CIdNumber: this.s1IdNumber,
      CPhone: this.s1Phone,
      CCaptchaCode: this.s1CaptchaCode,
      CCaptchaToken: this.s1CaptchaToken,
      CVerifCode: this.s1VerifCode,
      Agreement: this.s1AgreeChecked
    }).subscribe(
      (res: any) => {
        if (res?.StatusCode === EnumStatusCode.Success && res?.Entries?.IsSuccess) {
          this.stepToken = res.Entries?.StepToken || '';
          // ✅ Step1 驗證成功 → 進入 Step 2
          this.currentStep = 2;
          this.loadStep2DropdownOptions();
          this.s1ErrorMsg = '';
          this.goTop();
        } else {
          // ❌ 驗證失敗 → 顯示錯誤訊息
          this.s1ErrorMsg = res.Message || res.ErrorMsg || '驗證失敗，請稍後重試。';
        }
      },
      error => {
        this.s1ErrorMsg = this.resolveApiErrorMessage(error, '❌ 伺服器連線失敗，請稍後重試。');
      }
    );
  }

  // ── Step 2 → Step 3 ────────────────────────────────────────────────────────

  step2Submit() {
    this.s2ErrorMsg = '';
    if (!this.s2IssueDate.trim()) {
      this.s2ErrorMsg = '請輸入發證日期（格式：民國年月日，如 1100101）。';
      return;
    }
    if (!this.s2IssueLocation) {
      this.s2ErrorMsg = '請選擇發證地點。';
      return;
    }
    if (!this.s2IssueCategory) {
      this.s2ErrorMsg = '請選擇領補換類別。';
      return;
    }

    // 轉換西元年月日為民國格式的出生日期（從 Step1 取得）
    // 假設 s1BirthDate 為西元年月日格式，或由 HTML 中的隱藏欄位提供
    this.s2Validating = true;

    this.userService.VerifyStep2({
      StepToken: this.stepToken,
      PersonId: this.s1IdNumber,
      BirthYyymmdd: '0700101',  // TODO: 從 Step1 或身分證卡面獲取出生日期，轉為民國格式
      ApplyCode: this.s2IssueCategory,
      ApplyYyymmdd: this.s2IssueDate,  // 已經是民國格式的發證日期
      IssueSiteId: this.s2IssueLocation
    }).subscribe({
      next: (res: any) => {
        this.s2Validating = false;
        if (res.StatusCode === EnumStatusCode.Success && res.Entries?.IsSuccess) {
          // ✅ 驗證成功 → 進入 Step 3
          this.currentStep = 3;
          this.loadStep3Captcha();
          this.goTop();
        } else {
          // ❌ 驗證失敗 → 顯示錯誤訊息
          this.s2ErrorMsg = res.Entries?.Message || res.Message || '身份驗證失敗，請確認資訊後重試。';
        }
      },
      error: (error: any) => {
        this.s2Validating = false;
        this.s2ErrorMsg = this.resolveApiErrorMessage(error, '驗證服務暫時無法使用，請稍後再試。');
      }
    });
  }

  // ── Step 3：取得 Email 驗證碼 ─────────────────────────────────────────────
  getStep3EmailCode() {
    this.s3ErrorMsg = '';
    if (!this.s3Email.trim()) {
      this.s3ErrorMsg = '請輸入 Email。';
      return;
    }
    if (!/^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/.test(this.s3Email)) {
      this.s3ErrorMsg = 'Email 格式錯誤。';
      return;
    }
    this.s3Submitting = true;
    this.userService.SendEmailCode({
      StepToken: this.stepToken,
      CIdNumber: this.s1IdNumber,
      Email: this.s3Email,
      CustomerName: '客戶'
    }).subscribe({
      next: (res: any) => {
        this.s3Submitting = false;
        if (res.StatusCode === EnumStatusCode.Success) {
          this.s3EmailSent = true;
          if (res.Entries?.VerifyCode) {
            alert(`[開發模式] Email 驗證碼：${res.Entries.VerifyCode}`);
          }
        } else {
          this.s3ErrorMsg = res.Message || 'Email 發送失敗，請稍後重試。';
        }
      },
      error: (error: any) => {
        this.s3Submitting = false;
        this.s3ErrorMsg = this.resolveApiErrorMessage(error, '❌ 伺服器連線失敗，請稍後重試。');
      }
    });
  }

  // ── Step 3 → 確認對話框 ───────────────────────────────────────────────────
  step3Next() {
    this.s3ErrorMsg = '';
    if (!this.s3Email.trim()) {
      this.s3ErrorMsg = '請輸入 Email。';
      return;
    }
    if (!/^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/.test(this.s3Email)) {
      this.s3ErrorMsg = 'Email 格式錯誤。';
      return;
    }
    if (!this.s3CaptchaCode.trim()) {
      this.s3ErrorMsg = '請輸入圖形驗證碼。';
      return;
    }
    if (!this.s3VerifCode.trim()) {
      this.s3ErrorMsg = '請輸入 Email 驗證碼。';
      return;
    }
    if (!this.s3EmailSent) {
      this.s3ErrorMsg = '請先取得 Email 驗證碼。';
      return;
    }
    this.s3ShowConfirm = true;
  }

  step3ConfirmCancel() {
    this.s3ShowConfirm = false;
  }

  // ── Step 3 → Step 4（確認送出）──────────────────────────────────────────
  step3Submit() {
    this.s3ShowConfirm = false;
    this.s3Submitting = true;
    this.s3ErrorMsg = '';

    this.userService.VerifyEmailCode({
      StepToken: this.stepToken,
      CIdNumber: this.s1IdNumber,
      Email: this.s3Email,
      VerifyCode: this.s3VerifCode
    }).subscribe({
      next: (res: any) => {
        if (res.StatusCode === EnumStatusCode.Success) {
          this.submitEBillApplication();
        } else {
          this.s3Submitting = false;
          this.s3ErrorMsg = res.Message || 'Email 驗證失敗，請檢查驗證碼。';
        }
      },
      error: (error: any) => {
        this.s3Submitting = false;
        this.s3ErrorMsg = this.resolveApiErrorMessage(error, '❌ 伺服器連線失敗，請稍後重試。');
      }
    });
  }

  private submitEBillApplication() {
    this.userService.SubmitEBillApply({
      StepToken: this.stepToken,
      cIdNumber: this.s1IdNumber,
      cPhone: this.s1Phone,
      cID_IssueDate: this.s2IssueDate,
      cID_Place: this.s2IssueLocation || '',
      cID_Type: this.s2IssueCategory || '',
      cEmail: this.s3Email
    }).subscribe({
      next: (res: any) => {
        this.s3Submitting = false;
        if (res.StatusCode === EnumStatusCode.Success) {
          this.currentStep = 4;
          this.goTop();
        } else {
          this.s3ErrorMsg = res.Message || '申請提交失敗，請稍後重試。';
        }
      },
      error: () => {
        this.s3Submitting = false;
        this.s3ErrorMsg = '❌ 伺服器連線失敗，請稍後重試。';
      }
    });
  }

  // ── 重設整個流程 ────────────────────────────────────────────────────────────

  reset() {
    this.currentStep = 1;
    this.s1IdNumber = '';
    this.s1Phone = '';
    this.s1CaptchaCode = '';
    this.s1VerifCode = '';
    this.s1AgreeChecked = false;
    this.s1SmsSent = false;
    this.s1ErrorMsg = '';
    this.stepToken = '';
    this.s2IssueDate = '';
    this.s2IssueLocation = null;
    this.s2IssueCategory = null;
    this.issueSiteList = [];
    this.issueCategoryList = [];
    this.s2ErrorMsg = '';
    this.s2Validating = false;
    this.s3Email = '';
    this.s3CaptchaUrl = '';
    this.s3CaptchaToken = '';
    this.s3CaptchaCode = '';
    this.s3VerifCode = '';
    this.s3EmailSent = false;
    this.s3ShowConfirm = false;
    this.s3Submitting = false;
    this.s3ErrorMsg = '';
    this.loadStep1Captcha();
    this.goTop();
  }

  goTop() {
    if (this.isBrowser) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}